import { useState } from 'react';
import { InfoIcon } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { Switch } from '~/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';

/**
 * FRONT-2566: the "Listed for colleagues" switch, nested under Public in the
 * Share control and shown only once Public is the saved audience (decided
 * with the product owner: the box stays short until it applies).
 *
 * Listing is not access. On, colleagues find the app in Apps, search and
 * EDDY; off, it leaves their lists while the link keeps working for anyone.
 * The label is the server's `APP_LISTING_LABEL` (`src/apps/audience.ts`),
 * which EDDY quotes when it points an owner here — keep the two equal.
 */
export const LISTING_LABEL = 'Listed for colleagues';

export const LISTING_EXPLAINER =
  'On: people in your workspace find this app in Apps, in search and when they ask EDDY. ' +
  'Off: it disappears from their lists. The link keeps working for anyone either way.';

/** The visible line under the switch. Visible, not only in the tooltip: a
 *  hover hint reaches nobody on a phone, and this is the state the owner
 *  needs to read back. */
export function listingStateLine(listed: boolean): string {
  return listed
    ? 'Colleagues find it in Apps.'
    : 'Hidden from colleagues’ lists. Only people with the link can open it.';
}

export function PublicListingToggle({
  listed,
  disabled,
  onChange,
}: {
  listed: boolean;
  /** Locked or a write already in flight. */
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  // Controlled so a TAP opens it too: Radix opens a tooltip on hover and
  // keyboard focus only, and closes it on click. `preventDefault` on our own
  // click skips Radix's close handler. A tap OPENS rather than toggles: on
  // touch the focus that comes with the tap has usually opened it already,
  // and a toggle would close it again on the same tap. A tap outside or
  // Escape closes it.
  const [hintOpen, setHintOpen] = useState(false);

  return (
    <div className="ml-[26px] mr-1.5 mb-1 mt-0.5 border-l border-border pl-3 py-1.5 space-y-1">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1 min-w-0">
          <label htmlFor="public-listing-switch" className="text-sm font-medium cursor-pointer">
            {LISTING_LABEL}
          </label>
          <Tooltip open={hintOpen} onOpenChange={setHintOpen}>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`About ${LISTING_LABEL}`}
                onClick={(e) => {
                  e.preventDefault();
                  setHintOpen(true);
                }}
                className="relative inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring before:absolute before:-inset-2.5 before:content-['']"
              >
                <InfoIcon size={14} weight="bold" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[260px] text-pretty">{LISTING_EXPLAINER}</TooltipContent>
          </Tooltip>
        </span>
        <Switch
          id="public-listing-switch"
          checked={listed}
          disabled={disabled}
          onCheckedChange={onChange}
          // The primitive is 20x36; extend the press target to 40px tall
          // without moving the layout, and give it the house press feedback.
          className="relative active:scale-[0.96] transition-[background-color,scale] before:absolute before:-inset-y-2.5 before:-inset-x-1 before:content-['']"
        />
      </div>
      <p className="text-xs text-muted-foreground text-pretty">{listingStateLine(listed)}</p>
    </div>
  );
}

/**
 * Should a surface show the owner the "Unlisted" tag? Only for a Public app
 * the CALLER owns whose listing is confirmed off — `listedToColleagues` is
 * null for every non-owner (unknown, never "unlisted"), so a colleague can
 * never be shown a tag about someone else's choice.
 */
export function showsUnlistedTag(app: {
  audience: string;
  isOwner?: boolean;
  listedToColleagues?: boolean | null;
}): boolean {
  return app.audience === 'public' && app.isOwner !== false && app.listedToColleagues === false;
}

export const UNLISTED_TAG_LABEL = 'Unlisted';

/** The small neutral tag beside a Public badge. Neutral on purpose: it is a
 *  narrowing of Public, not a warning of its own. */
export function UnlistedTag({ className }: { className?: string }) {
  return (
    <span
      data-testid="unlisted-tag"
      className={cn(
        'inline-flex items-center rounded-full border border-border bg-muted px-1.5 py-px text-[length:var(--text-2xs)] uppercase tracking-wide text-muted-foreground',
        className,
      )}
    >
      {UNLISTED_TAG_LABEL}
    </span>
  );
}
